/**
 * Preview Helpers
 *
 * Normalize different data shapes (submission, title, formData) into TitlePreviewData
 */

import type { ApiTitle } from "@lumi/data";
import type { TitlePreviewData } from "../pages/ReviewStep/TitlePreview";

/**
 * Submission data shape (from provider hooks)
 */
export interface SubmissionData {
  title: string;
  posterUrl?: string | null;
  backdropUrl?: string | null;
  logoUrl?: string | null;
  horizontalTileUrl?: string | null;
  verticalTileUrl?: string | null;
  year?: number | null;
  releaseYear?: number | null;
  languages?: string[];
  country?: string | null;
  resolution?: string | null;
  runtime?: number | null;
  durationMinutes?: number | null;
  genres?: string[];
  genreData?: string[];
  directorData?: Array<{ name: string }>;
  director?: string;
  synopsis?: string | null;
  rating?: string;
}

/**
 * FormData shape (from CreateSubmission wizard)
 */
export interface FormData {
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  logoUrl?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  releaseYear?: number;
  language?: string | string[]; // Primary field used by forms (supports multi-select)
  languages?: string[]; // Deprecated - fallback for backward compatibility
  country?: string;
  resolution?: string;
  runtime?: number;
  genres?: string[];
  director?: string;
  synopsis?: string;
  rating?: string;
}

/**
 * Normalize submission data to preview format
 */
export function submissionToPreview(submission: SubmissionData): TitlePreviewData {
  // Extract director name - handle array format
  let director: string | undefined;
  if (submission.directorData && Array.isArray(submission.directorData)) {
    director = submission.directorData[0]?.name;
  } else if (submission.director) {
    director = submission.director;
  }

  // Extract genres - handle both genres and genreData
  const genres = submission.genreData || submission.genres;

  // Extract runtime - handle both fields
  const runtime = submission.runtime || submission.durationMinutes;

  // Extract year - handle both fields
  const year = submission.year || submission.releaseYear;

  return {
    title: submission.title,
    backdropUrl: submission.backdropUrl || undefined,
    logoUrl: submission.logoUrl || undefined,
    posterUrl: submission.posterUrl || undefined,
    horizontalTileUrl: submission.horizontalTileUrl || undefined,
    verticalTileUrl: submission.verticalTileUrl || undefined,
    year: year || undefined,
    resolution: submission.resolution || undefined,
    languages: submission.languages,
    country: submission.country || undefined,
    runtime: runtime || undefined,
    genres,
    director,
    synopsis: submission.synopsis || undefined,
    rating: submission.rating,
  };
}

/**
 * Normalize published title data to preview format
 */
export function titleToPreview(title: ApiTitle): TitlePreviewData {
  // Calculate runtime from available fields
  const runtime = title.runtimeMinutes || (title.durationSec ? Math.round(title.durationSec / 60) : undefined);

  // Use best available backdrop image (filter out nulls)
  const backdropUrl = title.backdropUrlHd ?? title.heroUrl ?? title.posterUrlHd ?? title.posterUrl ?? undefined;

  return {
    title: title.name,
    backdropUrl,
    logoUrl: title.logoUrl ?? undefined,
    posterUrl: title.posterUrlHd ?? title.posterUrl ?? undefined,
    horizontalTileUrl: title.heroUrl ?? title.backdropUrlHd ?? undefined,
    verticalTileUrl: title.posterUrlHd ?? title.posterUrl ?? undefined,
    year: title.year ?? undefined,
    resolution: title.resolution ?? undefined,
    languages: title.languages ?? undefined,
    runtime,
    genres: title.genres ?? undefined,
    director: title.directorName ?? undefined,
    synopsis: title.synopsis ?? undefined,
  };
}

/**
 * Normalize form data (from CreateSubmission wizard) to preview format
 */
export function formDataToPreview(formData: FormData): TitlePreviewData {
  // Normalize language field: formData uses 'language' but preview expects 'languages'
  let normalizedLanguages: string[] | undefined;
  if (Array.isArray(formData.language)) {
    normalizedLanguages = formData.language;
  } else if (formData.language) {
    normalizedLanguages = [formData.language];
  } else if (formData.languages) {
    // Fallback to deprecated languages field if present
    normalizedLanguages = formData.languages;
  }

  return {
    title: formData.title || "Untitled",
    backdropUrl: formData.backdropUrl,
    logoUrl: formData.logoUrl,
    posterUrl: formData.posterUrl,
    horizontalTileUrl: formData.horizontalTileUrl,
    verticalTileUrl: formData.verticalTileUrl,
    year: formData.releaseYear,
    resolution: formData.resolution,
    languages: normalizedLanguages,
    country: formData.country,
    runtime: formData.runtime,
    genres: formData.genres,
    director: formData.director,
    synopsis: formData.synopsis,
    rating: formData.rating,
  };
}
